import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { SuperSEO } from "react-super-seo";
import * as _ from "lodash";

// Import configurations
import getSelfDriveCars from "../config/selfDriveCars";
import getWithDriverCars from "../config/withDriverCars";
import getSelfDriveBikes from "../config/selfDriveBikes";
import getLocations from "../config/locations";

function ServiceLanding() {
    const { serviceType } = useParams();
    const navigate = useNavigate();
    const [selectedLocation, setSelectedLocation] = useState("");
    const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);

    useEffect(() => {
        window.scrollTo(0, 0);
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [serviceType]);

    // Service mappings configuration
    const serviceConfigs: Record<string, {
        title: string;
        bannerTitle: string;
        description: string;
        bannerImg: string;
        aboutTitle: string;
        aboutDesc: string;
        features: Array<{ icon: string; title: string; desc: string }>;
        faqs: Array<{ q: string; a: string }>;
        locations: string[];
        fleetImgFolder: 'cars' | 'bikes' | null;
        getFleet: () => any[];
        ctaPlaceholder: string;
    }> = {
        "self-driving-car-rental": {
            title: "Self-Drive Car Rentals in Maharashtra | Aapli Gaadi",
            bannerTitle: "Premium Self-Drive Car Rentals",
            description: "Rent premium self-drive cars in Mumbai, Thane, Navi Mumbai, and the Konkan coast. Enjoy transparent pricing and 24/7 support.",
            bannerImg: "/image/bg_1.jpg",
            aboutTitle: "Why Choose Our Self-Drive Cars?",
            aboutDesc: "Skip the chauffeur and take the wheel! With Aapli Gaadi, you rent clean, fully insured self-drive cars at the cheapest daily rates. Perfect for weekend road trips, family vacations, or running city errands.",
            features: [
                { icon: "fa-solid fa-gauge-high", title: "Flexible KM Packages", desc: "Choose from different KM allowances depending on your trip distance." },
                { icon: "fa-solid fa-shield", title: "Comprehensive Insurance", desc: "All our vehicles come with comprehensive insurance coverage." },
                { icon: "fa-solid fa-road", title: "24/7 Roadside Help", desc: "Get round-the-clock roadside assistance wherever you drive in Maharashtra." },
                { icon: "fa-solid fa-clock", title: "Flexible Mileage Plans", desc: "Select daily range options tailored to your specific travel route." }
            ],
            faqs: [
                { q: "What documents are required to rent a self-drive car?", a: "You need a valid driving license (held for at least 2 years), an Aadhaar card (identity proof), and a local address proof." },
                { q: "Is fuel included in the rental charges?", a: "No, fuel is not included. You receive the car with some fuel level and must return it with the same level, or buy additional fuel as you go." },
                { q: "What is the minimum age to rent a self-drive car?", a: "The driver must be at least 25 years old and possess a valid driving license (held for at least 2 years)." }
            ],
            locations: ["Mumbai", "Navi Mumbai", "Thane", "Kudal", "Malvan", "Tarkarli", "Devbag", "Vaibhavwadi", "Kankavli", "Kunkeshwar", "Devgad", "Ratnagiri", "Ganpatipule", "Sawantwadi", "Vengurla"],
            fleetImgFolder: "cars",
            getFleet: () => getSelfDriveCars(),
            ctaPlaceholder: "Select Pickup Location"
        },
        "car-rental": {
            title: "Chauffeur Driven Car Rentals in Maharashtra | Aapli Gaadi",
            bannerTitle: "Chauffeur-Driven Car Rentals",
            description: "Hire professional chauffeur-driven car rentals across Mumbai and Konkan regions. Experienced local drivers, sanitised vehicles, and fixed packages.",
            bannerImg: "/image/bg_3.jpg",
            aboutTitle: "Ride in Comfort with Local Chauffeurs",
            aboutDesc: "Don't want to navigate long highways or narrow coastal roads? Sit back and let our professional, fully verified drivers take you to your destination. Best suited for family temple tours, sightseeing, and business trips.",
            features: [
                { icon: "fa-solid fa-user-check", title: "Professional Chauffeurs", desc: "Our drivers are highly experienced, polite, and have excellent route knowledge." },
                { icon: "fa-solid fa-broom", title: "Sanitized Clean Fleets", desc: "Every car is thoroughly cleaned, deodorized, and sanitized before every trip." },
                { icon: "fa-solid fa-clock", title: "Punctual Door-step Pickups", desc: "We respect your time. Enjoy zero delays with punctual pickups directly from your home/hotel." },
                { icon: "fa-solid fa-receipt", title: "Transparent Pricing", desc: "Fixed packages with no hidden driver bata, toll, or surprise parking charges." }
            ],
            faqs: [
                { q: "Are driver allowances and meals included in the package?", a: "Yes, our package charges include the driver allowance. You do not need to pay extra driver allowances, although meals are appreciated if traveling long distances." },
                { q: "Can we use chauffeur cars for outstation round trips?", a: "Yes, you can hire chauffeur cars for local city runs, airport pickups, or multi-day outstation round trips." },
                { q: "What is the cancellation policy for chauffeur rentals?", a: "You can cancel up to 24 hours before your journey for a full refund." }
            ],
            locations: ["Mumbai", "Navi Mumbai", "Thane", "Kudal", "Malvan", "Tarkarli", "Devbag", "Vaibhavwadi", "Kankavli", "Kunkeshwar", "Devgad", "Ratnagiri", "Ganpatipule", "Sawantwadi", "Vengurla"],
            fleetImgFolder: "cars",
            getFleet: () => getWithDriverCars(),
            ctaPlaceholder: "Select Pickup Location"
        },
        "self-driving-bike-rental": {
            title: "Self-Drive Bike Rentals in Konkan & Kudal | Aapli Gaadi",
            bannerTitle: "Self-Drive Bike Rentals",
            description: "Rent budget scooters and motorbikes in Malvan, Tarkarli, and Kudal. Explore the gorgeous coastal routes, beaches, and local trails on two wheels.",
            bannerImg: "/image/bg_2.jpg",
            aboutTitle: "Explore the Beautiful Coast on Two Wheels",
            aboutDesc: "Feel the sea breeze as you cruise along the scenic marine drive pathways of Konkan! Rent high-mileage scooters (Activa, Jupiter) or motorbikes from our hubs. Budget-friendly, reliable, and perfect for beach hopping.",
            features: [
                { icon: "fa-solid fa-wallet", title: "Extremely Economical", desc: "Rates start as low as ₹600/day. Best option for budget solo or couple travellers." },
                { icon: "fa-solid fa-motorcycle", title: "High-Mileage Fleet", desc: "Well-maintained, fuel-efficient scooters and bikes ready for long rides." },
                { icon: "fa-solid fa-location-dot", title: "Easy Beach Hopping", desc: "Park easily near beach shacks, explore narrow alleys, and scenic viewpoints." },
                { icon: "fa-solid fa-helmet-safety", title: "Sanitized Gear Provided", desc: "Every rental comes with sanitized safety helmets for both rider and pillion." }
            ],
            faqs: [
                { q: "Do I need to carry my own helmet?", a: "We provide one sanitized safety helmet with the bike rental. Additional helmets can be requested at the time of pickup." },
                { q: "Is there a limit on KMs for bike rentals?", a: "Bikes come with standard daily KM packages (e.g. 100km/day). Extra KM charges apply if you exceed this limit." },
                { q: "What are the rules regarding driving license?", a: "A valid two-wheeler driving license is mandatory. Learner licenses are not accepted." }
            ],
            locations: ["Kudal", "Malvan", "Tarkarli", "Devbag", "Ratnagiri", "Ganpatipule", "Sawantwadi", "Vengurla"],
            fleetImgFolder: "bikes",
            getFleet: () => getSelfDriveBikes(),
            ctaPlaceholder: "Select Hub Location"
        },
        "pick-and-drop": {
            title: "Station & Airport Pick & Drop Services | Aapli Gaadi",
            bannerTitle: "Airport & Railway Station Transfers",
            description: "Reliable and punctual pickup and drop transfer services in Maharashtra. Connect comfortably between airports, railway stations, hotels, and tourist hubs.",
            bannerImg: "/image/aapli_gaadi_locations.png",
            aboutTitle: "Seamless Station & Airport Transfers",
            aboutDesc: "Arriving at Mopa Airport or Kudal Railway Station? We provide hassle-free pickup and drop transfers in comfortable AC vehicles. Our drivers handle your luggage and coordinate schedules so you arrive relaxed.",
            features: [
                { icon: "fa-solid fa-plane-departure", title: "Airport Pickups & Drops", desc: "Direct transfers to/from Goa/Mopa Airport, Chipi Airport (Sindhudurg), and Konkan region." },
                { icon: "fa-solid fa-train", title: "Railway Station Transfers", desc: "Timely drops and pickups at Kudal, Sawantwadi, and Kankavli railway stations." },
                { icon: "fa-solid fa-suitcase-rolling", title: "Luggage Assistance", desc: "Our driver assists you with luggage loading and unloading at all checkpoints." },
                { icon: "fa-solid fa-circle-exclamation", title: "Flight & Train Tracking", desc: "We track your flight/train status in real time to adjust pickup timings for delays." }
            ],
            faqs: [
                { q: "How do I book a pick & drop service?", a: "Select your arrival city, input dates/timings, and place a query. Our booking executive will confirm vehicle details via WhatsApp." },
                { q: "Will the driver wait if my train or flight is delayed?", a: "Yes, we monitor arrival timings. The driver will wait for you without additional waiting charges for up to 90 minutes." },
                { q: "What types of cars are used for transfers?", a: "We use clean, air-conditioned sedans (like Dzire, Aura) or spacious MPVs (like Ertiga, Innova) depending on guest count." }
            ],
            locations: ["Kudal", "Malvan", "Tarkarli", "Devbag", "Vaibhavwadi", "Kankavli", "Kunkeshwar", "Devgad", "Ratnagiri", "Ganpatipule", "Sawantwadi", "Vengurla"],
            fleetImgFolder: null,
            getFleet: () => [],
            ctaPlaceholder: "Select Transfer Location"
        }
    };

    const currentConfig = serviceConfigs[serviceType || ""] || serviceConfigs["self-driving-car-rental"];

    // Search submit handler
    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (selectedLocation) {
            const formattedSlug = _.kebabCase(selectedLocation);
            
            // For pick-and-drop, we can redirect them to contact or book via WhatsApp
            if (serviceType === "pick-and-drop") {
                const message = `Hello Aapli Gaadi, I want to book a Pick & Drop transfer service at ${selectedLocation}. Please share vehicle options and price list!`;
                const whatsappUrl = `https://wa.me/917208388380?text=${encodeURIComponent(message)}`;
                window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
            } else {
                navigate(`/services/${serviceType}/${formattedSlug}`);
            }
        }
    };

    // Load dynamic fleet
    const fleet = currentConfig.getFleet().slice(0, 3); // top 3 for showcase

    return (
        <>
            <SuperSEO
                title={currentConfig.title}
                description={currentConfig.description}
                lang="en"
            />

            {/* Banner Section */}
            <section className="hero-wrap hero-wrap-2 js-fullheight" style={{ backgroundImage: `url('${currentConfig.bannerImg}')`, height: '420px', backgroundPosition: 'center', backgroundSize: 'cover', position: 'relative' }}>
                <div className="overlay" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#000', opacity: 0.55 }}></div>
                <div className="container" style={{ position: 'relative', height: '100%' }}>
                    <div className="row no-gutters slider-text justify-content-start align-items-center" style={{ height: '420px' }}>
                        <div className="col-md-9 text-white pt-5">
                            <p className="breadcrumbs mb-2" style={{ fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                <span className="mr-2">
                                    <Link to="/" style={{ color: '#fff' }}>Home</Link> <span className="mx-2">&gt;</span>
                                </span>
                                <span className="mr-2">
                                    <Link to="/services" style={{ color: '#fff' }}>Services</Link> <span className="mx-2">&gt;</span>
                                </span>
                                <span>
                                    <span style={{ color: '#01d28e' }}>{currentConfig.bannerTitle}</span>
                                </span>
                            </p>
                            <h1 className="mb-3 text-white font-weight-bold" style={{ fontSize: '38px' }}>
                                {currentConfig.bannerTitle}
                            </h1>
                            <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '15px', maxWidth: '600px' }} className="mb-4">
                                {currentConfig.description}
                            </p>

                            {/* Booking Search Form */}
                            <form onSubmit={handleSearchSubmit} className="d-flex flex-wrap align-items-center bg-white p-3 rounded shadow-sm text-dark mt-4" style={{ maxWidth: '500px', gap: '10px' }}>
                                <div className="flex-grow-1">
                                    <select 
                                        className="form-control" 
                                        required 
                                        value={selectedLocation} 
                                        onChange={(e) => setSelectedLocation(e.target.value)}
                                        style={{ height: '44px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', cursor: 'pointer' }}
                                    >
                                        <option value="">{currentConfig.ctaPlaceholder}</option>
                                        {currentConfig.locations.map((loc, idx) => (
                                            <option value={loc} key={idx}>{loc}</option>
                                        ))}
                                    </select>
                                </div>
                                <button 
                                    type="submit" 
                                    className="btn btn-primary px-4 font-weight-bold"
                                    style={{ height: '44px', borderRadius: '6px', fontSize: '14px', minWidth: '130px' }}
                                >
                                    {serviceType === "pick-and-drop" ? "Book Transfer" : "Find Rides"}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Core Overview Section */}
            <section className="ftco-section bg-white" style={{ padding: '5em 0' }}>
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-md-6 mb-4 mb-md-0">
                            <h2 className="font-weight-bold text-dark mb-3" style={{ fontSize: '28px' }}>{currentConfig.aboutTitle}</h2>
                            <p className="text-muted" style={{ fontSize: '15px', lineHeight: '1.7' }}>
                                {currentConfig.aboutDesc}
                            </p>
                            <p className="text-muted" style={{ fontSize: '15px', lineHeight: '1.7' }}>
                                All booking ledgers are maintained locally, and pricing packages come with transparent pricing, zero surprise fees, and flexible extensions via WhatsApp.
                            </p>
                        </div>
                        <div className="col-md-6">
                            <div className="row">
                                {currentConfig.features.map((feat, idx) => (
                                    <div className="col-sm-6 mb-4" key={idx}>
                                        <div className="p-3 bg-light rounded" style={{ height: '100%', border: '1px solid #f1f5f9' }}>
                                            <div className="d-flex align-items-center justify-content-center rounded mb-2 text-primary" style={{ width: '40px', height: '40px', backgroundColor: 'rgba(1,210,142,0.1)' }}>
                                                <i className={feat.icon} style={{ fontSize: '18px' }}></i>
                                            </div>
                                            <h4 className="font-weight-bold text-dark h6 mb-1">{feat.title}</h4>
                                            <p className="text-muted small mb-0">{feat.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Dynamic Featured Fleet Section */}
            {fleet.length > 0 && (
                <section className="ftco-section bg-light border-top border-bottom" style={{ padding: '5em 0' }}>
                    <div className="container">
                        <div className="row justify-content-center mb-5">
                            <div className="col-md-7 text-center heading-section">
                                <span className="subheading" style={{ color: '#01d28e', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '1.5px', fontSize: '13px' }}>Our Fleet</span>
                                <h2 className="mb-2 font-weight-bold" style={{ fontSize: '28px', color: '#0f172a' }}>Featured Vehicles</h2>
                                <p className="text-muted small">A glimpse of our well-maintained fleet available for this service.</p>
                            </div>
                        </div>

                        <div className="row justify-content-center">
                            {fleet.map((vehicle: any, idx: number) => {
                                const vehicleSlug = _.kebabCase(`${vehicle.Brand}-${vehicle.Name}-${vehicle.isSelfDrive ? 'self-drive' : 'with-driver'}`);
                                const defaultCity = vehicle.Locations[0] || "mumbai";
                                const detailUrl = `/services/${serviceType}/${_.kebabCase(defaultCity)}/${vehicleSlug}`;

                                return (
                                    <div className="col-md-4 mb-4" key={idx}>
                                        <div className="card shadow-sm border-0 rounded overflow-hidden" style={{ height: '100%', backgroundColor: '#ffffff' }}>
                                            <div style={{ height: '200px', overflow: 'hidden', position: 'relative', backgroundColor: '#f8fafc' }}>
                                                <img 
                                                    src={`${process.env.PUBLIC_URL}/image/${currentConfig.fleetImgFolder}/${vehicle.Image}`} 
                                                    alt={`${vehicle.Brand} ${vehicle.Name}`} 
                                                    style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '10px' }}
                                                />
                                            </div>
                                            <div className="card-body p-4 d-flex flex-column justify-content-between">
                                                <div>
                                                    <h3 className="h6 font-weight-bold text-dark mb-2" style={{ fontSize: '16px' }}>{vehicle.Brand} {vehicle.Name}</h3>
                                                    <div className="d-flex text-muted small mb-3" style={{ gap: '15px' }}>
                                                        <span><i className="fa-solid fa-gas-pump mr-1"></i> {vehicle.Details.Fuel}</span>
                                                        <span><i className="fa-solid fa-gears mr-1"></i> {vehicle.Details.Transmission}</span>
                                                        <span><i className="fa-solid fa-users mr-1"></i> {vehicle.Details.SeatingCapacity} seats</span>
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center justify-content-between pt-3 border-top mt-3">
                                                    <div>
                                                        <span className="text-primary font-weight-bold h5 mb-0">₹{vehicle.Charges[0]}</span>
                                                        <span className="text-muted small"> / Day</span>
                                                    </div>
                                                    <Link to={detailUrl} className="btn btn-outline-primary btn-sm px-3" style={{ borderRadius: '20px', fontWeight: 600, fontSize: '12px' }}>
                                                        Book Now
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* SEO Link directory specific to this service */}
            <section className="ftco-section bg-white" style={{ padding: '5em 0' }}>
                <div className="container">
                    <div className="row justify-content-center mb-5">
                        <div className="col-md-8 text-center heading-section">
                            <h2 className="mb-2 font-weight-bold" style={{ fontSize: '26px', color: '#0f172a' }}>Available Locations for this Service</h2>
                            <p className="text-muted small">We serve multiple cities across Maharashtra. Click below to view available fleet and customize bookings in that area.</p>
                        </div>
                    </div>

                    <div className="bg-light p-4 rounded shadow-sm" style={{ border: '1px solid #e2e8f0' }}>
                        <div className="row" style={{ fontSize: '14px', lineHeight: '2.4' }}>
                            {_.chunk(currentConfig.locations, Math.ceil(currentConfig.locations.length / 3)).map((chunk, chunkIdx) => (
                                <div className="col-md-4" key={chunkIdx}>
                                    <ul className="list-unstyled mb-0">
                                        {chunk.map((loc, idx) => {
                                            const formattedLoc = _.kebabCase(loc);
                                            const targetUrl = serviceType === "pick-and-drop" 
                                                ? `https://wa.me/917208388380?text=${encodeURIComponent(`Hi Aapli Gaadi, I want to book a pick and drop transfer in ${loc}`)}`
                                                : `/services/${serviceType}/${formattedLoc}`;
                                            
                                            return (
                                                <li key={idx}>
                                                    {serviceType === "pick-and-drop" ? (
                                                        <a href={targetUrl} target="_blank" rel="noopener noreferrer" className="text-muted hover-primary">
                                                            📍 {loc} Station Transfer →
                                                        </a>
                                                    ) : (
                                                        <Link to={targetUrl} className="text-muted hover-primary">
                                                            📍 {loc} Hub Listings →
                                                        </Link>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQs Section */}
            <section className="ftco-section bg-light border-top" style={{ padding: '5em 0' }}>
                <div className="container">
                    <div className="row justify-content-center mb-5">
                        <div className="col-md-7 text-center heading-section">
                            <span className="subheading" style={{ color: '#01d28e', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '1.5px', fontSize: '13px' }}>FAQ</span>
                            <h2 className="mb-2 font-weight-bold" style={{ fontSize: '28px', color: '#0f172a' }}>Frequently Asked Questions</h2>
                            <p className="text-muted small">Answers to common queries regarding this specific service.</p>
                        </div>
                    </div>

                    <div className="row justify-content-center">
                        <div className="col-md-8">
                            {currentConfig.faqs.map((faq, idx) => (
                                <div className="bg-white p-4 rounded mb-3 shadow-sm" style={{ border: '1px solid #e2e8f0' }} key={idx}>
                                    <h4 className="font-weight-bold text-dark h6 mb-2 d-flex align-items-start">
                                        <span className="text-success mr-2">Q.</span>
                                        <span>{faq.q}</span>
                                    </h4>
                                    <p className="text-muted mb-0 pl-3 small" style={{ lineHeight: '1.6' }}>
                                        {faq.a}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}

export default ServiceLanding;
